import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://cammesa-servidor-770702622935.us-central1.run.app';

/** kVA = sqrt(kW² + kVAR²) */
export function calcKva(kw, kvar) {
  const k = Number(kw) || 0;
  const r = Number(kvar) || 0;
  return Math.sqrt(k * k + r * r);
}

/** Normaliza un registro de BD a campos de UI (kVA/kVAR corregidos) */
export function normalizeMedicion(item) {
  const kwDesp = Number(item.kwGen || item.kwDesp || item.potencia_activa || 0);
  const fp = Number(item.factor_potencia || item.cosfi || 0);
  const stored = Number(item.kVAR || item.kVARDesp || item.potencia_reactiva || 0);

  let kVA = Number(item.kVA || item.kva || item.potencia_aparente) || 0;
  let kVAR = stored;

  // En planta el campo kVAR de energia1 suele guardar kVA; recalcular con FP si está disponible
  if (fp > 0.01 && kwDesp > 0) {
    kVA = kwDesp / fp;
    kVAR = Math.sqrt(Math.max(0, kVA * kVA - kwDesp * kwDesp));
  } else if (!kVA && stored > kwDesp && kwDesp > 0) {
    kVA = stored;
    kVAR = Math.sqrt(Math.max(0, kVA * kVA - kwDesp * kwDesp));
  } else if (!kVA) {
    kVA = calcKva(kwDesp, kVAR);
  }

  return {
    timestamp: item.timestamp || item.fecha_hora || new Date().toISOString(),
    kwDesp,
    kVARDesp: kVAR,
    kVAR,
    kVA,
    cosfi: fp,
    factor_potencia: fp,
    frecuencia: Number(item.frecuencia || 0),
    tension_vab: Number(item.tension_vab || item.tension_l1 || 0),
    tension_vbc: Number(item.tension_vbc || item.tension_l2 || 0),
    tension_vca: Number(item.tension_vca || item.tension_l3 || 0),
    tension_l1: Number(item.tension_vab || item.tension_l1 || 0),
    tension_l2: Number(item.tension_vbc || item.tension_l2 || 0),
    tension_l3: Number(item.tension_vca || item.tension_l3 || 0),
    tension_promedio: Number(item.tension_promedio || 0),
    corriente_ia: Number(item.corriente_ia || item.corriente_l1 || 0),
    corriente_ib: Number(item.corriente_ib || item.corriente_l2 || 0),
    corriente_ic: Number(item.corriente_ic || item.corriente_l3 || 0),
    corriente_l1: Number(item.corriente_ia || item.corriente_l1 || 0),
    corriente_l2: Number(item.corriente_ib || item.corriente_l2 || 0),
    corriente_l3: Number(item.corriente_ic || item.corriente_l3 || 0),
    corriente: Number(item.corriente || item.corriente_ia || 0)
  };
}

// Debug: Mostrar la URL que se está usando
console.log('🔧 Dashboard configurado con API URL:', API_URL);
console.log('🔧 REACT_APP_API_URL =', process.env.REACT_APP_API_URL);

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: `${API_URL}/api`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    this.fastClient = axios.create({
      baseURL: `${API_URL}/api`,
      timeout: 8000,
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  // Health check
  async getHealth() {
    const response = await this.client.get('/health');
    const data = response.data || {};
    const connections = data.connections || {};
    // Normaliza para que el Dashboard muestre el estado de DB
    return {
      ...data,
      connections: {
        ...connections,
        database: connections.database ?? connections.mysql
      }
    };
  }

  // Plantas
  async getPlantas() {
    try {
      const response = await this.fastClient.get('/plantas');
      if (Array.isArray(response.data) && response.data.length > 0) {
        return response.data.map(p => ({
          id: p.id || p.equipo || p.nombre || 'gvbio',
          nombre: p.nombre || p.id || 'Planta',
          tipo_generacion: p.tipo_generacion || 'Generación'
        }));
      }
    } catch (e) {
      console.warn('GET /plantas no disponible, usando health');
    }
    try {
      const health = await this.getHealth();
      const ids = Array.isArray(health.plantas) && health.plantas.length
        ? health.plantas
        : [(health?.lastMeasurement?.equipo) || 'gvbio'];
      return ids.map(id => ({
        id,
        nombre: id === 'gvbio' ? 'GV Bio' : String(id),
        tipo_generacion: 'Generación'
      }));
    } catch (e) {
      return [{ id: 'gvbio', nombre: 'GV Bio', tipo_generacion: 'Generación' }];
    }
  }

  // Última medición (rápida, para tarjetas en tiempo real)
  async getUltimaMedicion() {
    try {
      const response = await this.fastClient.get('/mediciones', { params: { limit: 1 } });
      const data = response.data || [];
      if (data.length > 0) return normalizeMedicion(data[0]);
    } catch (error) {
      console.error('Error fetching ultima medicion:', error);
    }
    try {
      const health = await this.getHealth();
      if (health.lastMeasurement) return normalizeMedicion(health.lastMeasurement);
    } catch (e) {
      // ignore
    }
    return null;
  }

  // Mediciones históricas
  async getMediciones(plantaId = 'planta', limit = 50, horas = null) {
    try {
      const params = { limit };
      if (horas) {
        params.horas = horas;
      }

      const response = await this.client.get('/mediciones', { params });
      const data = response.data || [];

      return data.map(item => normalizeMedicion(item));
    } catch (error) {
      console.error('Error fetching mediciones:', error);
      return [];
    }
  }

  // Alarmas
  async getAlarmas() {
    const response = await this.client.get('/alarmas');
    return response.data;
  }

  async reconocerAlarma(alarmaId, usuario) {
    const response = await this.client.post(`/alarmas/${alarmaId}/reconocer`, { usuario });
    return response.data;
  }

  // Órdenes
  async enviarOrden(plantaId, tipoOrden, parametros, operador) {
    const response = await this.client.post('/ordenes', {
      planta_id: plantaId,
      tipo_orden: tipoOrden,
      parametros,
      operador
    });
    return response.data;
  }

  async getOrdenes(plantaId = null) {
    const url = plantaId ? `/ordenes/${plantaId}` : '/ordenes';
    const response = await this.client.get(url);
    return response.data;
  }

  // Estadísticas
  async getEstadisticas(plantaId, horas = 24) {
    const response = await this.client.get(`/estadisticas/${plantaId}`, {
      params: { horas }
    });
    return response.data;
  }

  // Novedades
  async getNovedades(plantaId = null) {
    const url = plantaId ? `/novedades/${plantaId}` : '/novedades';
    const response = await this.client.get(url);
    return response.data;
  }

  async marcarNovedadLeida(novedadId, usuario) {
    const response = await this.client.post(`/novedades/${novedadId}/leer`, { usuario });
    return response.data;
  }
  // ===== REPORTES =====
  async getReporteDiario(fecha) {
    try {
      const response = await this.client.get('/reportes/diario', {
        params: { fecha }
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching reporte diario:', error);
      return [];
    }
  }
}

export const apiService = new ApiService();
export default apiService;
